import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { BoreholeCardSvg } from './BoreholeCardSvg';
import type { BoreholeCardModel } from './cardTypes';

interface Props {
  model: BoreholeCardModel;
  mapImage: string | null;
  logoImage: string | null;
  /** Ref na kontener głównego SVG — używany przez rodzica do serializacji do PDF. */
  frameRef: RefObject<HTMLDivElement>;
  /** Klik w element podglądu z atrybutem `data-field` → przejście do odpowiedniego pola w edytorze. */
  onFieldPick?: (fid: string) => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const STEP = 0.25;
const DBL_ZOOM = 2.5;        // przybliżenie po dwukliku
const BASE_MAX = 640;        // szerokość karty przy 100% (jak dotychczas)
const CARD_RATIO = 1123 / 794; // wysokość / szerokość A4 (do proporcji minimapy)
const MINI_W = 132;          // szerokość minimapy [px]

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const clamp01 = (v: number) => clamp(v, 0, 1);

/** Widoczny wycinek karty jako frakcje 0..1 (do prostokąta na minimapie). */
interface View { left: number; top: number; w: number; h: number }

/**
 * Podgląd karty otworu z przybliżaniem i minimapką.
 * - przybliżanie: przyciski, Ctrl/⌘ + kółko (zakotwiczone w kursorze), dwuklik;
 * - przesuwanie: przeciąganie myszą (gdy przybliżone);
 * - minimapa: w prawym górnym rogu, tylko gdy przybliżone; klik/przeciągnięcie recentruje.
 * `frameRef` wskazuje kontener głównego SVG — minimapa renderuje osobną kopię poza nim,
 * żeby serializacja PDF w rodzicu nadal łapała właściwy `<svg>`.
 */
export function CardPreview({ model, mapImage, logoImage, frameRef, onFieldPick }: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [view, setView] = useState<View>({ left: 0, top: 0, w: 1, h: 1 });
  // zapamiętany punkt zakotwiczenia przybliżenia (frakcja karty + offset kursora w viewport)
  const pending = useRef<{ fx: number; fy: number; px: number; py: number } | null>(null);
  // rozróżnienie klik/przeciągnięcie (żeby pan nie wywoływał przejścia do pola) oraz
  // opóźnienie kliku, by dwuklik (przybliżenie) nie wykonywał też przejścia
  const moved = useRef(false);
  const clickTimer = useRef<number | null>(null);

  const zoomed = zoom > 1.001;

  // przelicz prostokąt widoku z metryk przewijania (względem kontenera karty)
  const syncView = useCallback(() => {
    const vp = viewportRef.current, frame = frameRef.current;
    if (!vp || !frame) return;
    const fw = frame.offsetWidth || 1, fh = frame.offsetHeight || 1;
    setView({
      left: clamp01((vp.scrollLeft - frame.offsetLeft) / fw),
      top: clamp01((vp.scrollTop - frame.offsetTop) / fh),
      w: clamp01(vp.clientWidth / fw),
      h: clamp01(vp.clientHeight / fh),
    });
  }, [frameRef]);

  // po zmianie przybliżenia: przywróć zakotwiczenie i odśwież widok (layout już zaktualizowany)
  useLayoutEffect(() => {
    const vp = viewportRef.current, frame = frameRef.current;
    if (!vp || !frame) return;
    const p = pending.current;
    if (p) {
      const left = p.fx * frame.offsetWidth + frame.offsetLeft - p.px;
      const top = p.fy * frame.offsetHeight + frame.offsetTop - p.py;
      vp.scrollLeft = clamp(left, 0, vp.scrollWidth - vp.clientWidth);
      vp.scrollTop = clamp(top, 0, vp.scrollHeight - vp.clientHeight);
      pending.current = null;
    }
    syncView();
  }, [zoom, syncView]);

  // odśwież minimapę gdy zmienia się rozmiar karty (np. dodano warstwy) lub viewportu
  useEffect(() => {
    const vp = viewportRef.current, frame = frameRef.current;
    if (!vp || !frame || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => syncView());
    ro.observe(vp); ro.observe(frame);
    return () => ro.disconnect();
  }, [syncView, frameRef]);

  // ustaw przybliżenie z zakotwiczeniem w punkcie (clientX/clientY); domyślnie środek viewportu
  const zoomTo = useCallback((next: number, clientX?: number, clientY?: number) => {
    const vp = viewportRef.current, frame = frameRef.current;
    const z = clamp(Math.round(next * 100) / 100, MIN_ZOOM, MAX_ZOOM);
    if (vp && frame) {
      const rect = vp.getBoundingClientRect();
      const px = clientX != null ? clientX - rect.left : vp.clientWidth / 2;
      const py = clientY != null ? clientY - rect.top : vp.clientHeight / 2;
      pending.current = {
        fx: (vp.scrollLeft + px - frame.offsetLeft) / (frame.offsetWidth || 1),
        fy: (vp.scrollTop + py - frame.offsetTop) / (frame.offsetHeight || 1),
        px, py,
      };
    }
    setZoom(z);
  }, [frameRef]);

  // Ctrl/⌘ + kółko = przybliżanie (natywny listener, bo React używa pasywnego wheel)
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return; // bez modyfikatora — normalne przewijanie
      e.preventDefault();
      zoomTo(zoom + (e.deltaY < 0 ? STEP : -STEP), e.clientX, e.clientY);
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [zoom, zoomTo]);

  useEffect(() => () => { if (clickTimer.current != null) window.clearTimeout(clickTimer.current); }, []);

  const onDoubleClick = (e: React.MouseEvent) => {
    // anuluj oczekujące przejście do pola z pojedynczego kliku — dwuklik służy do przybliżenia
    if (clickTimer.current != null) { window.clearTimeout(clickTimer.current); clickTimer.current = null; }
    zoomTo(zoomed ? 1 : DBL_ZOOM, e.clientX, e.clientY);
  };

  // klik w element z `data-field` → przejście do pola (z opóźnieniem, by nie kolidować z dwuklikiem;
  // pominięte, gdy klik był w rzeczywistości przeciągnięciem/panem)
  const onClick = (e: React.MouseEvent) => {
    if (moved.current) { moved.current = false; return; }
    if (!onFieldPick) return;
    const el = (e.target as Element)?.closest?.('[data-field]');
    const fid = el?.getAttribute('data-field');
    if (!fid) return;
    if (clickTimer.current != null) window.clearTimeout(clickTimer.current);
    // opóźnienie ≈ typowy odstęp dwukliku, by dwuklik (przybliżenie) zdążył anulować przejście do pola
    clickTimer.current = window.setTimeout(() => { clickTimer.current = null; onFieldPick(fid); }, 280);
  };

  // przeciąganie = przesuwanie (tylko gdy przybliżone)
  const onPanStart = (e: React.MouseEvent) => {
    moved.current = false;
    const vp = viewportRef.current;
    if (!zoomed || e.button !== 0 || !vp) return;
    const sx = e.clientX, sy = e.clientY, sl = vp.scrollLeft, st = vp.scrollTop;
    setDragging(true);
    const move = (ev: MouseEvent) => {
      if (Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) > 4) moved.current = true;
      vp.scrollLeft = sl - (ev.clientX - sx);
      vp.scrollTop = st - (ev.clientY - sy);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    e.preventDefault();
  };

  // klik/przeciągnięcie po minimapie = recentrowanie viewportu
  const onMiniStart = (e: React.MouseEvent) => {
    const mini = e.currentTarget as HTMLDivElement;
    const recenter = (clientX: number, clientY: number) => {
      const vp = viewportRef.current, frame = frameRef.current;
      if (!vp || !frame) return;
      const rect = mini.getBoundingClientRect();
      const fx = clamp01((clientX - rect.left) / rect.width);
      const fy = clamp01((clientY - rect.top) / rect.height);
      vp.scrollLeft = clamp(fx * frame.offsetWidth + frame.offsetLeft - vp.clientWidth / 2, 0, vp.scrollWidth - vp.clientWidth);
      vp.scrollTop = clamp(fy * frame.offsetHeight + frame.offsetTop - vp.clientHeight / 2, 0, vp.scrollHeight - vp.clientHeight);
      syncView();
    };
    recenter(e.clientX, e.clientY);
    const move = (ev: MouseEvent) => recenter(ev.clientX, ev.clientY);
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    e.preventDefault();
    e.stopPropagation();
  };

  const pct = Math.round(zoom * 100);

  return (
    <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
      <style>{`
        .card-frame svg{width:100%;height:auto;display:block}
        .card-mini svg{width:100%;height:auto;display:block}
        .card-frame [data-field]{cursor:pointer}
      `}</style>

      <div
        ref={viewportRef}
        onScroll={syncView}
        onMouseDown={onPanStart}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        style={{
          position: 'absolute', inset: 0, overflow: 'auto', background: '#e2e8f0', padding: 16,
          cursor: zoomed ? (dragging ? 'grabbing' : 'grab') : 'default',
          userSelect: dragging ? 'none' : 'auto',
        }}
      >
        <div
          ref={frameRef}
          className="card-frame"
          style={{
            background: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,0.18)', margin: '0 auto',
            width: `calc(min(100%, ${BASE_MAX}px) * ${zoom})`,
          }}
        >
          <BoreholeCardSvg model={model} mapImage={mapImage} logoImage={logoImage} />
        </div>
      </div>

      {/* pasek narzędzi przybliżania */}
      <div style={{
        position: 'absolute', top: 12, left: 12, display: 'flex', alignItems: 'center', gap: 2,
        background: 'rgba(255,255,255,0.94)', border: '1px solid #cbd5e1', borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)', padding: 2,
      }}>
        <button style={zBtn} title="Oddal (Ctrl + kółko)" disabled={zoom <= MIN_ZOOM}
          onClick={() => zoomTo(zoom - STEP)}>−</button>
        <button style={{ ...zBtn, width: 52, fontVariantNumeric: 'tabular-nums' }}
          title="Dopasuj do okna" onClick={() => zoomTo(1)}>{pct}%</button>
        <button style={zBtn} title="Przybliż (Ctrl + kółko)" disabled={zoom >= MAX_ZOOM}
          onClick={() => zoomTo(zoom + STEP)}>+</button>
      </div>

      {/* minimapa — tylko gdy przybliżone */}
      {zoomed && (
        <div
          className="card-mini"
          onMouseDown={onMiniStart}
          style={{
            position: 'absolute', top: 12, right: 12, width: MINI_W, height: Math.round(MINI_W * CARD_RATIO),
            background: '#fff', border: '1px solid #94a3b8', borderRadius: 6, overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.22)', cursor: 'pointer',
          }}
          title="Minimapa — kliknij, aby przejść do fragmentu"
        >
          <BoreholeCardSvg model={model} mapImage={mapImage} logoImage={logoImage} />
          {/* przyciemnienie poza widocznym wycinkiem + ramka wycinka */}
          <div style={{
            position: 'absolute', pointerEvents: 'none',
            left: `${view.left * 100}%`, top: `${view.top * 100}%`,
            width: `${view.w * 100}%`, height: `${view.h * 100}%`,
            border: '2px solid #0369a1', background: 'rgba(3,105,161,0.14)',
            boxShadow: '0 0 0 9999px rgba(15,23,42,0.28)',
          }} />
        </div>
      )}
    </div>
  );
}

const zBtn: React.CSSProperties = {
  width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer',
  fontSize: 14, fontWeight: 600, color: '#334155',
};
